import { TestParserOptions } from '../../options/testParserOptions.js';
import { Test, TestType } from '../../parser/models/all.js';
import { PostProcessContext } from '../models/postProcessContext.js';

/**
 * Post-processor that expands class instance methods with test cases into individual test entries.
 * 
 * @param test - The test representing a class method with parameterized cases.
 * @param index - The index of the test in the collection.
 * @param context - The post-processing context.
 * @returns True if the test was processed and expanded.
 */
export function processClassInstTestCases(test: Test, index: number, context: PostProcessContext): boolean {

  if (test.type.has(TestType.CLASS_INST_FUNCTION_WITH_CASES)) {
    const testCaseProperties = createClassInstTestCases(
      test,
      context.moduleToParse,
      context.options
    );

    context.postTests.push(...testCaseProperties);

    return true;
  }

  return false;
}

/**
 * Creates individual Test instances for each parameterized case of a class instance method.
 * 
 * @param test - The template test containing the method and cases.
 * @param classInstance - The instance to bind the method to.
 * @param options - Parser options for title templating.
 * @returns An array of expanded Test instances.
 */
function createClassInstTestCases(test: Test, classInstance: any, options: TestParserOptions): Test[] {
  const { name, value: testFn, testCases } = test;

  const results: Test[] = [];
  testCases.forEach(
    (testCasesArgs, testCaseIndex) => {
      const testCaseArgs = [...testCasesArgs, testCaseIndex];
      const testCaseFn = testFn.bind(classInstance, ...testCaseArgs);
      const newTestCase = new Test(
        test.groupName,
        options.testCaseTitleTemplate(name, testCasesArgs, testCaseIndex),
        testCaseFn,
        test.type,
        test.testCases,
        test.onlyExpression
      );
      results.push(newTestCase);
    }
  )

  return results;
}